/* eslint-disable react-refresh/only-export-components -- archivo comparte helpers/constantes; split diferido */
import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

type Tema = 'light' | 'dark' | 'system';

interface ContextoTemaType {
  tema: Tema;
  alternarTema: () => void;
  establecerTema: (tema: Tema) => void;
}

const ContextoTema = createContext<ContextoTemaType | undefined>(undefined);

interface ProveedorContextoTemaProps {
  children: ReactNode;
}

export function ProveedorContextoTema({ children }: ProveedorContextoTemaProps) {
  const [tema, setTema] = useState<Tema>(() => {
    // Verificar si hay un tema guardado en localStorage
    const temGuardado = localStorage.getItem('tema') as Tema;
    if (temGuardado && ['light', 'dark', 'system'].includes(temGuardado)) {
      return temGuardado;
    }
    
    // Por defecto usar 'system'
    return 'system';
  });

  const alternarTema = () => {
    setTema(temAnterior => {
      switch (temAnterior) {
        case 'light': return 'dark';
        case 'dark': return 'system';
        case 'system': return 'light';
        default: return 'light';
      }
    });
  };

  const establecerTema = (nuevoTema: Tema) => {
    setTema(nuevoTema);
  };

  // Función para obtener el tema efectivo
  const obtenerTemaEfectivo = (temActual: Tema) => {
    if (temActual === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return temActual;
  };

  useEffect(() => {    
    // Deshabilitar transiciones temporalmente para cambio instantáneo
    document.documentElement.classList.add('changing-theme');
    
    const temaEfectivo = obtenerTemaEfectivo(tema);        
    
    // Aplicar tema al documento usando data-theme attribute (para semantic tokens)
    if (temaEfectivo === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');      
    } else {
      document.documentElement.setAttribute('data-theme', 'light');      
    }  
    
    // Guardar tema en localStorage
    localStorage.setItem('tema', tema);
    
    // Rehabilitar transiciones después de un frame
    requestAnimationFrame(() => {
      document.documentElement.classList.remove('changing-theme');
    });
  }, [tema]);

  // Escuchar cambios en las preferencias del sistema solo cuando está en modo 'system'
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = () => {
      // Solo actualizar si el tema está en modo 'system'
      if (tema === 'system') {
        const temaEfectivo = obtenerTemaEfectivo(tema);
        if (temaEfectivo === 'dark') {
          document.documentElement.setAttribute('data-theme', 'dark');
        } else {
          document.documentElement.setAttribute('data-theme', 'light');
        }
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [tema]);

  return (
    <ContextoTema.Provider value={{ tema, alternarTema, establecerTema }}>
      {children}
    </ContextoTema.Provider>
  );
}

export function useContextoTema() {
  const context = useContext(ContextoTema);
  if (context === undefined) {
    throw new Error('useContextoTema debe usarse dentro de ProveedorContextoTema');
  }
  return context;
}
